import { Grid, Paper, Typography, Box } from "@mui/material";
import ProductImage from "@/components/productImage";
import { PurchaseResultZinc } from "@t3/shared";
import convertToPoints from "@/utils/convertToPoints";

interface CartHistoryZincItemProps {
  results: PurchaseResultZinc;
}

type OrderStatus = "canceled" | "processing" | "error" | "success";

const StatusMessage = ({
  status,
  message,
}: {
  status: OrderStatus;
  message: string;
}) => {
  const statusConfig = {
    canceled: {
      color: "#d32f2f",
      bgcolor: "#fef3f2",
      label: "Canceled",
    },
    processing: {
      color: "#1976d2",
      bgcolor: "#f0f7ff",
      label: "Processing",
    },
    error: {
      color: "#d32f2f",
      bgcolor: "#fef3f2",
      label: "Error",
    },
    success: {
      color: "#2e7d32",
      bgcolor: "#f1f8f4",
      label: "Success",
    },
  };

  const config = statusConfig[status];

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: config.bgcolor,
        borderRadius: 1,
        borderLeft: `4px solid ${config.color}`,
        mb: 2,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: config.color,
          fontWeight: 500,
          fontSize: "0.875rem",
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

export const CartHistoryZincItem = ({ results }: CartHistoryZincItemProps) => {
  // Early return if no products
  if (!results.zincProducts?.length) return null;

  // Extract order data
  const orderData = results.orderData;
  const isSuccessType = orderData?.responseType === "order_response";
  const isFailureType = orderData?.responseType === "error";
  const isProcessing = !orderData;

  return (
    <>
      {results.zincProducts.map((product, index) => {
        const productId = `${product.product_id}_${index}`;

        // Find the delivery date for this specific product
        const productDeliveryDate = orderData?.deliveryDates?.find(
          (dd) => dd.product_id === product.product_id,
        );

        // Find tracking info for this specific product
        const productTrackingInfo = orderData?.trackingInfo?.filter(
          (tracking) => tracking.product_ids?.includes(product.product_id),
        );
        const hasProductTracking =
          productTrackingInfo && productTrackingInfo.length > 0;

        return (
          <Paper
            elevation={0}
            sx={{
              margin: "16px 0",
              padding: 3,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
            key={productId}
          >
            <Grid container spacing={2} direction={{ xs: "column", sm: "row" }}>
              {/* Product Image */}
              <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "150px",
                    margin: "0 auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <ProductImage card={product} width="100%" height="auto" />
                </Box>
              </Grid>

              {/* Product Details */}
              <Grid size={{ xs: 12, sm: 9, md: 10 }}>
                {/* Product Title */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#212121",
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  {product.title || "Product"}
                  {product.quantity && product.quantity > 1 && (
                    <Typography
                      component="span"
                      sx={{
                        ml: 1,
                        color: "#757575",
                        fontWeight: 400,
                      }}
                    >
                      (Qty: {product.quantity})
                    </Typography>
                  )}
                </Typography>

                {isFailureType && (
                  <StatusMessage
                    status="error"
                    message="Unable to process order. Points have been refunded."
                  />
                )}

                {isProcessing && (
                  <StatusMessage
                    status="processing"
                    message="Your order is currently processing. Check back later for tracking information."
                  />
                )}

                {/* Tracking Section */}
                {isSuccessType && productDeliveryDate && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: "#fafafa",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    {/* Expected Delivery - Always Show */}
                    <Box sx={{ mb: hasProductTracking ? 1.5 : 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#757575",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Expected Delivery
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#212121",
                          fontWeight: 500,
                        }}
                      >
                        {productDeliveryDate.date || "Date pending"}
                      </Typography>
                    </Box>

                    {/* Tracking Status */}
                    {hasProductTracking ? (
                      productTrackingInfo?.map((tracking, idx) => {
                        const deliveryStatus =
                          tracking.delivery_status ||
                          "No delivery status available";
                        const isDeliveredStatus = deliveryStatus
                          .toLowerCase()
                          .includes("delivered");

                        return (
                          <Box
                            key={idx}
                            sx={{
                              pt: idx === 0 ? 1.5 : 0,
                              borderTop:
                                idx === 0 ? "1px solid #e0e0e0" : "none",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#757575",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              Current Status
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDeliveredStatus
                                  ? "#2e7d32"
                                  : "#212121",
                                fontWeight: isDeliveredStatus ? 600 : 500,
                              }}
                            >
                              {deliveryStatus}
                            </Typography>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#757575",
                          fontSize: "0.875rem",
                          fontStyle: "italic",
                        }}
                      >
                        Tracking information will be available soon
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Delivery Email */}
                {results.email && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#757575",
                        fontSize: "0.75rem",
                        display: "block",
                        mb: 0.25,
                      }}
                    >
                      Delivery email
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#212121", fontWeight: 500 }}
                    >
                      {results.email}
                    </Typography>
                  </Box>
                )}

                {/* Reference ID and Points */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 2,
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#757575",
                        fontSize: "0.75rem",
                        display: "block",
                        mb: 0.25,
                      }}
                    >
                      Reference ID
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        color: "#212121",
                        fontSize: "0.875rem",
                      }}
                    >
                      {orderData?.referenceOrderID ||
                        results.referenceOrderID ||
                        "Not available"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      textAlign: "right",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      height: "100%",
                      margin: "auto",
                      marginRight: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#757575",
                        fontSize: "0.75rem",
                      }}
                    >
                      Points
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#d32f2f",
                        fontWeight: 600,
                      }}
                    >
                      {Math.round(
                        convertToPoints(product.amount) * product.quantity,
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        );
      })}
    </>
  );
};
